import {useDeleteReconciliationMutation} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type DeleteReconciliationModalUserProps = {
  reconciliationId: string;
  /** ISO date of the reconciliation's ending anchor, for display. */
  anchorDate: string;
  transactionCount: number;
  onDeleted?: () => void;
};

export const DeleteReconciliationModal = (
  props: DeleteReconciliationModalUserProps & SystemProps,
) => {
  const {reconciliationId, anchorDate, transactionCount, onDeleted, onRequestClose} =
    props;
  const [deleteReconciliation, {loading}] = useDeleteReconciliationMutation();
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setError(null);
    try {
      await deleteReconciliation({variables: {reconciliationId}});
      onDeleted?.();
      onRequestClose();
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Failed to delete reconciliation',
      );
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="destructive"
        onClick={() => void handleDelete()}
        disabled={loading}
      >
        {loading ? 'Unreconciling...' : 'Unreconcile'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Unreconcile balance"
      footer={footer}
      size="sm"
    >
      {error && (
        <div className="bg-destructive/10 text-destructive mb-3 rounded-md p-3 text-sm">
          {error}
        </div>
      )}
      <p>
        Are you sure you want to unreconcile the balance for{' '}
        <strong>{anchorDate}</strong>? The balance itself is kept; this deletes
        the reconciliation and unlocks the{' '}
        {transactionCount === 1
          ? 'associated transaction'
          : `${transactionCount} associated transactions`}
        .
      </p>
    </Modal>
  );
};
